// src/components/AddTimeLogModal.js

import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Modal from "react-modal";
import PropTypes from "prop-types";
import Select from "react-select";

// Ensure accessibility for react-modal
Modal.setAppElement("#root");

const AddTimeLogModal = ({
  isOpen,
  onClose,
  onSubmit,
  opportunities,
  students = [],
}) => {
  // Replace studentName state with selectedStudent
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Focus on the first input when modal opens
      const timer = setTimeout(() => {
        const firstInput = document.getElementById("studentId");
        if (firstInput) firstInput.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Update validation to check for selectedStudent and selectedOpportunity
    if (
      !selectedStudent ||
      !selectedOpportunity ||
      !date ||
      !startTime ||
      !endTime
    ) {
      toast.error("Please fill in all fields.");
      return;
    }

    // Validate date (must not be in the future)
    const selectedDate = new Date(date);
    const today = new Date();
    if (selectedDate > today) {
      toast.error("Date cannot be in the future.");
      return;
    }

    // Validate start and end times
    const start = new Date(`${date}T${startTime}`);
    const end = new Date(`${date}T${endTime}`);
    if (start >= end) {
      toast.error("End Time must be after Start Time.");
      return;
    }

    // Calculate total hours
    const diffInMs = end - start;
    const totalHours = diffInMs / (1000 * 60 * 60);

    // Ensure total hours is within a reasonable range
    if (totalHours <= 0 || totalHours > 24) {
      toast.error("Total hours must be between 0 and 24.");
      return;
    }

    setLoading(true);
    try {
      const timeLog = {
        id: Date.now() + Math.random(),
        studentId: selectedStudent.value,
        opportunityId: selectedOpportunity.value,
        hostName: selectedOpportunity.label,
        date,
        startTime,
        endTime,
        totalHours,
        description,
        status: "pending",
      };

      // Just pass the timeLog - we don't need to pass studentId separately
      // since it's already included in the timeLog object
      await onSubmit(timeLog);

      // Reset form fields
      setSelectedStudent(null);
      setSelectedOpportunity(null);
      setDate("");
      setStartTime("");
      setEndTime("");
      setDescription("");
      onClose();
    } catch (error) {
      toast.error("Failed to add time log. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Ensure students is an array
  const safeStudents = Array.isArray(students) ? students : [];

  // Create options for the Select component with ID as value and name as label
  const studentOptions = safeStudents.map((student) => ({
    value: student.studentId,
    label: `[${student.studentId}] ${student.firstName} ${student.lastName}`,
  }));

  const safeOpportunities = Array.isArray(opportunities) ? opportunities : [];

  // Filter opportunities based on selected student
  const getFilteredOpportunities = () => {
    if (!selectedStudent) return [];

    return safeOpportunities
      .filter((opportunity) =>
        opportunity.students?.includes(selectedStudent.value)
      )
      .sort((a, b) => a.hostName.localeCompare(b.hostName));
  };

  // Create options for the Select component with filtered opportunities
  const opportunityOptions = getFilteredOpportunities().map((opportunity) => ({
    value: opportunity.id,
    label: opportunity.hostName,
  }));

  // Clear selected opportunity when student changes
  useEffect(() => {
    setSelectedOpportunity(null);
  }, [selectedStudent]);

  // Add function to handle start time change
  const handleStartTimeChange = (e) => {
    const newStartTime = e.target.value;
    setStartTime(newStartTime);

    // Calculate end time (start time + 1 hour)
    if (newStartTime) {
      const [hours, minutes] = newStartTime.split(":");
      const startDate = new Date();
      startDate.setHours(parseInt(hours), parseInt(minutes));
      const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);

      // Format end time as HH:mm
      const endHours = endDate.getHours().toString().padStart(2, "0");
      const endMinutes = endDate.getMinutes().toString().padStart(2, "0");
      setEndTime(`${endHours}:${endMinutes}`);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onRequestClose={onClose}
        contentLabel="Add Time Log"
        className="bg-white rounded-lg w-full max-w-md p-6 relative mx-auto my-auto outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 focus:outline-none"
          aria-label="Close Modal"
        >
          <X size={20} />
        </button>
        {/* Modal Title */}
        <h2 className="text-xl font-bold mb-4">Add New Time Log</h2>
        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Replace Student Name input with Select */}
          <div>
            <label htmlFor="studentId" className="block text-gray-700">
              Student
            </label>
            <Select
              id="studentId"
              options={studentOptions}
              value={selectedStudent}
              onChange={setSelectedStudent}
              placeholder="Select or type a student name or student ID"
              isClearable
              isSearchable
              className="react-select-container"
              classNamePrefix="react-select"
              noOptionsMessage={() => "No students available"}
            />
          </div>
          {/* Replace Host Name select with Select component */}
          <div>
            <label htmlFor="hostName" className="block text-gray-700">
              Host Name
            </label>
            <Select
              id="hostName"
              options={opportunityOptions}
              value={selectedOpportunity}
              onChange={setSelectedOpportunity}
              placeholder={
                selectedStudent
                  ? "Select or type a host name"
                  : "Please select a student first"
              }
              isClearable
              isSearchable
              isDisabled={!selectedStudent}
              className="react-select-container"
              classNamePrefix="react-select"
              noOptionsMessage={() =>
                selectedStudent
                  ? "No opportunities available for this student"
                  : "Please select a student first"
              }
            />
          </div>
          {/* Date */}
          <div>
            <label htmlFor="date" className="block text-gray-700">
              Date
            </label>
            <input
              id="date"
              type="date"
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>
          {/* Start Time */}
          <div className="flex justify-between gap-2">
            <div className="w-1/2">
              <label htmlFor="startTime" className="block text-gray-700">
                Start Time
              </label>
              <input
                id="startTime"
                type="time"
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={startTime}
                onChange={handleStartTimeChange}
                required
              />
            </div>
            {/* End Time */}
            <div className="w-1/2">
              <label htmlFor="endTime" className="block text-gray-700">
                End Time
              </label>
              <input
                id="endTime"
                type="time"
                className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                required
              />
            </div>
          </div>
          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-gray-700">
              Description
            </label>
            <textarea
              id="description"
              className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description"
              rows="4"
            ></textarea>
          </div>
          {/* Form Buttons */}
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded focus:outline-none"
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`bg-blue-500 text-white font-semibold py-2 px-4 rounded focus:outline-none ${
                loading ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-600"
              }`}
              disabled={loading}
            >
              {loading ? "Adding..." : "Add Time Log"}
            </button>
          </div>
        </form>
      </Modal>
      <ToastContainer />
    </>
  );
};

// Define PropTypes for better type checking
AddTimeLogModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  opportunities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ).isRequired,
  students: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default AddTimeLogModal;
